import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { api, formatMoney } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { brandFor, darkLogoFor } from "../lib/brand";
import Navbar from "../components/Navbar";
import { ShoppingBag, ChevronRight, Trash2, ChevronLeft, Search, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, remove, total } = useCart();
  useCurrency(); // subscribe so prices re-render on currency switch
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  // Filter cart items by SKU / name — useful when the cart has 20+ rows and
  // the user wants to find a single part quickly. Empty query = all items.
  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((i) =>
      (i.sku || "").toLowerCase().includes(needle) ||
      (i.name || "").toLowerCase().includes(needle),
    );
  }, [items, q]);

  return (
    <div>
      <Navbar />

      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/catalog">Kezdőlap</Link> / <Link to="/catalog">Katalógus</Link> /{" "}
          <span style={{ color: "var(--text)" }}>Bevásárlókosár</span>
        </div>
      </div>

      <main className="max-w-[1200px] mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-2 uppercase tracking-wide">Bevásárlókosár</h1>
        <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

        <AddProductSearch />

        {items.length === 0 ? (
          <div className="card-elev p-12 text-center" data-testid="cart-empty">
            <ShoppingBag size={44} className="mx-auto mb-3" style={{ color: "var(--text-disabled)" }} />
            <h3 className="font-bold text-lg mb-2">Az Ön kosara üres.</h3>
            <p className="text-sm mb-5" style={{ color: "var(--text-mute)" }}>
              Folytassa a vásárlást és találja meg a megfelelő alkatrészeket.
            </p>
            <Link to="/catalog" className="btn-primary"><ChevronLeft size={14} /> Vissza a katalógushoz</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_320px] gap-5 items-start">
            <section className="card-elev overflow-hidden" data-testid="cart-table">
              <div className="section-title flex items-center justify-between">
                <span>Termékek a kosarában ({items.length})</span>
                {items.length > 3 && (
                  <div className="relative flex items-center" onClick={(e) => e.stopPropagation()}>
                    <Search size={12} style={{ color: "rgba(255,255,255,0.7)", position: "absolute", left: 8 }} />
                    <input
                      type="text"
                      placeholder="Keresés a kosárban..."
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      className="text-xs pl-7 pr-2 py-1 rounded bg-white text-gray-800 outline-none w-48"
                      data-testid="cart-search"
                    />
                  </div>
                )}
              </div>
              <div className="overflow-x-auto bg-white">
                <table className="table-b2b">
                  <thead>
                    <tr>
                      <th style={{ width: 64 }}></th>
                      <th>Cikkszám / Megnevezés</th>
                      <th style={{ width: 110 }}>Mennyiség</th>
                      <th style={{ width: 110 }} className="text-right">Egységár</th>
                      <th style={{ width: 130 }} className="text-right">Tétel</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && q && (
                      <tr><td colSpan={6} className="text-center py-6 text-sm" style={{ color: "var(--text-mute)" }}>
                        Nincs a kosárban a keresett cikkszámmal egyező tétel.
                      </td></tr>
                    )}
                    {filtered.map((i) => {
                      const brand = brandFor(i);
                      return (
                        <tr key={i.product_id} data-testid={`cart-row-${i.sku}`}>
                          <td>
                            <div className="w-14 h-14 flex items-center justify-center border" style={{ borderColor: "var(--border)", background: "#fff" }}>
                              {i.image_url ? (
                                <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />
                              ) : brand ? (
                                <img src={darkLogoFor(brand)} alt={brand.label} className="max-h-[60%] max-w-[60%] object-contain opacity-80" />
                              ) : (
                                <span className="text-[10px] font-bold" style={{ color: "var(--text-disabled)" }}>OEM</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <Link
                              to={`/product/${i.product_id}`}
                              className="font-bold text-base hover:underline"
                              style={{ color: "var(--brand)" }}
                            >
                              {i.name}
                            </Link>
                            <div className="font-mono text-sm font-semibold mt-0.5" style={{ color: "var(--text)" }}>
                              Cikksz: {i.sku}
                            </div>
                          </td>
                          <td>
                            <div className="inline-flex items-center border" style={{ borderColor: "var(--border-hi)", borderRadius: 3, background: "#fff" }}>
                              <button
                                className="w-8 h-8 hover:bg-[color:var(--surface-elev)] text-base font-bold"
                                onClick={() => updateQty(i.product_id, i.quantity - 1)}
                                data-testid={`dec-${i.sku}`}
                              >−</button>
                              <input
                                type="number" min="1" value={i.quantity}
                                onChange={(e) => updateQty(i.product_id, parseInt(e.target.value) || 1)}
                                className="w-12 text-center bg-transparent outline-none text-base font-bold border-x"
                                style={{ borderColor: "var(--border-hi)", height: 32 }}
                                data-testid={`qty-${i.sku}`}
                              />
                              <button
                                className="w-8 h-8 hover:bg-[color:var(--surface-elev)] text-base font-bold"
                                onClick={() => updateQty(i.product_id, i.quantity + 1)}
                                data-testid={`inc-${i.sku}`}
                              >+</button>
                            </div>
                          </td>
                          <td className="text-right font-mono text-base">{formatMoney(i.unit_price)}</td>
                          <td className="text-right font-mono font-bold text-base" style={{ color: "var(--brand)" }}>
                            {formatMoney(i.unit_price * i.quantity)}
                          </td>
                          <td>
                            <button
                              onClick={() => remove(i.product_id)}
                              className="btn-danger !py-1 !px-2"
                              data-testid={`remove-${i.sku}`}
                              title="Eltávolítás"
                            >
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="card-elev overflow-hidden lg:sticky lg:top-4" data-testid="cart-summary">
              <div className="section-title">Összegzés</div>
              <div className="p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span style={{ color: "var(--text-mute)" }}>Részösszeg (nettó):</span>
                  <span className="font-mono">{formatMoney(total)}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: "var(--text-mute)" }}>Szállítás:</span>
                  <span className="text-xs italic" style={{ color: "var(--text-mute)" }}>Ajánlat alapján</span>
                </div>
                <div className="border-t pt-2 flex justify-between items-baseline" style={{ borderColor: "var(--border)" }}>
                  <span className="font-bold uppercase">Összesen:</span>
                  <span className="font-bold text-lg" style={{ color: "var(--brand)" }} data-testid="cart-total">
                    {formatMoney(total)}
                  </span>
                </div>

                <div className="text-[11px] pt-1" style={{ color: "var(--out-stock)" }}>
                  Az árak nettó árak, az ÁFA-t nem tartalmazzák.
                </div>
              </div>
              <div className="p-4 pt-0 space-y-2">
                <button
                  onClick={() => navigate("/checkout")}
                  className="btn-primary w-full"
                  data-testid="proceed-checkout-button"
                >
                  Tovább a pénztárhoz <ChevronRight size={14} />
                </button>
                <Link to="/catalog" className="btn-ghost w-full justify-center">
                  <ChevronLeft size={12} /> Vásárlás folytatása
                </Link>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

// Inline product search — lets the customer add more items without leaving
// the cart. Debounced; a monotonic sequence counter drops stale responses so
// out-of-order API replies can't overwrite the newest results.
function AddProductSearch() {
  const { add } = useCart();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) {
      seq.current++; // invalidate any in-flight request
      setResults([]);
      setOpen(false);
      setSearching(false);
      return;
    }
    const id = setTimeout(async () => {
      const mySeq = ++seq.current;
      setSearching(true);
      try {
        const { data } = await api.get("/products", { params: { q: needle, limit: 8 } });
        if (mySeq !== seq.current) return; // stale — drop
        setResults(data.items || []);
        setOpen(true);
      } catch {
        if (mySeq === seq.current) { setResults([]); setOpen(true); }
      } finally {
        if (mySeq === seq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  // Close the dropdown when clicking outside
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const addItem = (p) => {
    add(p, 1);
    toast.success(`${p.sku} kosárba téve`);
  };

  return (
    <div className="card-elev mb-5" style={{ overflow: "visible" }} data-testid="cart-add-search">
      <div className="section-title">Termék hozzáadása — keresés cikkszámra vagy megnevezésre</div>
      <div className="p-3 bg-white relative" ref={boxRef}>
        <div
          className="flex items-center gap-2 border rounded px-3 py-2"
          style={{ borderColor: "var(--border-hi)" }}
        >
          <Search size={16} style={{ color: "var(--text-mute)" }} />
          <input
            type="text"
            placeholder="Pl. 06A115561B vagy olajszűrő..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => { if (results.length > 0 || (q.trim().length >= 2 && !searching)) setOpen(true); }}
            className="flex-1 outline-none text-sm bg-transparent"
            data-testid="cart-add-search-input"
          />
          {searching && <span className="text-xs" style={{ color: "var(--text-mute)" }}>Keresés...</span>}
          {q && (
            <button onClick={() => setQ("")} style={{ color: "var(--text-mute)" }} data-testid="cart-add-search-clear">
              <X size={14} />
            </button>
          )}
        </div>

        {open && (
          <div
            className="absolute left-3 right-3 z-30 bg-white border shadow-lg max-h-[360px] overflow-y-auto"
            style={{ borderColor: "var(--border-hi)", top: "calc(100% - 6px)" }}
            data-testid="cart-add-search-results"
          >
            {results.length === 0 ? (
              <div className="p-4 text-sm text-center" style={{ color: "var(--text-mute)" }}>
                Nincs találat a keresésre.
              </div>
            ) : (
              results.map((p) => {
                const noPrice = !p.your_price || p.your_price === 0;
                return (
                  <div
                    key={p.id}
                    className="flex items-center gap-3 px-3 py-2 border-b hover:bg-slate-50"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`cart-add-result-${p.sku}`}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-mono text-sm font-bold" style={{ color: "var(--brand)" }}>{p.sku}</span>
                      <div className="text-xs truncate" style={{ color: "var(--text-mute)" }}>{p.name}</div>
                    </div>
                    <div className="font-mono text-sm font-bold whitespace-nowrap" style={{ color: "var(--brand)" }}>
                      {noPrice ? "Ajánlat" : formatMoney(p.your_price)}
                    </div>
                    <button
                      onClick={() => addItem(p)}
                      disabled={noPrice}
                      className="btn-primary !py-1.5 !px-3 text-xs font-bold whitespace-nowrap"
                      title={noPrice ? "Nincs ár megadva" : "Kosárba"}
                      data-testid={`cart-add-btn-${p.sku}`}
                    >
                      <Plus size={13} /> Kosárba
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
